import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  nodeVncSchema,
  paginatedSchema,
  sessionSchema,
  vncProbeResultSchema,
  vncSessionSchema,
  type NodeVnc,
  type UpdateNodeVnc,
  type VncProbeResult,
  type VncSession,
} from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';

export const vncSettingsKey = (nodeId: string) => ['vnc', nodeId] as const;
export const sessionsKey = (active: boolean) => ['sessions', active] as const;
export const permittedNodesKey = ['vnc', 'permitted-nodes'] as const;

/** Параметры подключения узла и результат последнего зондирования. */
export function useNodeVnc(nodeId: string | null) {
  return useQuery({
    queryKey: vncSettingsKey(nodeId ?? ''),
    enabled: nodeId !== null,
    queryFn: () => apiRequest<NodeVnc>(`/nodes/${nodeId ?? ''}/vnc`, nodeVncSchema),
  });
}

/**
 * Изменение параметров подключения.
 *
 * Правка показывается сразу, до ответа сервера. Иначе флажок «только
 * просмотр» отказывается двигаться под пальцем: он привязан к тому, что
 * лежит в кэше, а туда значение попадает лишь после круга к серверу —
 * с проверкой прав и записью в журнал. Человек в это время нажимает
 * второй раз, решив, что промахнулся.
 *
 * Отказ возвращает прежнее значение: показанное оптимистично иначе
 * осталось бы на экране как настоящее.
 */
export function useUpdateNodeVnc(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateNodeVnc) =>
      apiRequest(`/nodes/${nodeId}/vnc`, nodeVncSchema, { method: 'PUT', body: input }),
    onMutate: async (input) => {
      // Идущий запрос отменяется: его ответ старше правки и затёр бы её.
      await queryClient.cancelQueries({ queryKey: vncSettingsKey(nodeId) });

      const previous = queryClient.getQueryData<NodeVnc>(vncSettingsKey(nodeId));

      queryClient.setQueryData(vncSettingsKey(nodeId), (current?: NodeVnc) =>
        current ? { ...current, ...input } : current,
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(vncSettingsKey(nodeId), context.previous);
      }
    },
    onSuccess: (settings) => queryClient.setQueryData(vncSettingsKey(nodeId), settings),
  });
}

/**
 * Зондирование по кнопке (§5.1, задача 6).
 *
 * Результат кладётся в кэш параметров узла: он и есть их часть, и лишний
 * запрос за тем, что уже пришло в ответе, был бы просто задержкой.
 */
export function useProbeVnc(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiRequest<VncProbeResult>(`/nodes/${nodeId}/vnc/probe`, vncProbeResultSchema, {
        method: 'POST',
      }),
    onSuccess: (result) =>
      queryClient.setQueryData(vncSettingsKey(nodeId), (previous?: NodeVnc) =>
        previous
          ? {
              ...previous,
              detectedKind: result.detectedKind,
              detectedVersion: result.detectedVersion,
              rfbVersion: result.rfbVersion,
              securityTypes: result.securityTypes,
              supportsFileTransfer: result.supportsFileTransfer,
              lastProbeAt: result.at,
              lastProbeError: result.error,
            }
          : previous,
      ),
  });
}

/**
 * Открытие сессии.
 *
 * Обычная функция, а не хук: сессию берёт тот, кто подключается, и в момент
 * подключения — то есть эффект, который создаёт соединение. Иначе тикет
 * живёт в состоянии компонента дольше, чем попытка подключения, и переживает
 * её: React вправе смонтировать эффект дважды, а тикет одноразовый (§10.1),
 * и второе подключение получит законный отказ вместо экрана.
 */
export async function openVncSession(nodeId: string): Promise<VncSession> {
  return apiRequest<VncSession>(`/nodes/${nodeId}/vnc/session`, vncSessionSchema, {
    method: 'POST',
  });
}

/**
 * Закрытие сессии, которую так и не открыли.
 *
 * Нужно ровно там, где попытку бросили между выдачей тикета и подключением:
 * тикет истечёт сам через тридцать секунд, а запись сессии осталась бы
 * открытой навсегда и заняла бы место в пределе одновременных.
 */
export async function closeVncSession(sessionId: string): Promise<void> {
  await apiRequest(`/sessions/${sessionId}`, z.unknown(), {
    method: 'DELETE',
    acceptStatuses: [204],
  });
}

export function useSessions(active = true, enabled = true) {
  return useQuery({
    queryKey: sessionsKey(active),
    enabled,
    // Экран администратора должен показывать происходящее сейчас, а не то,
    // что было при открытии вкладки.
    refetchInterval: 5_000,
    queryFn: () => apiRequest(`/sessions?active=${String(active)}`, paginatedSchema(sessionSchema)),
  });
}

export function useTerminateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      apiRequest(`/sessions/${sessionId}`, z.unknown(), {
        method: 'DELETE',
        acceptStatuses: [204],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

/**
 * Узлы, на которых право подключаться выдано точечно (§7.6).
 *
 * Нужны кнопке подключения на карте: спрашивать сервер по узлу на каждую
 * карточку — это сотня запросов на открытие площадки.
 */
export function usePermittedNodes(enabled = true) {
  return useQuery({
    queryKey: permittedNodesKey,
    enabled,
    staleTime: 60_000,
    queryFn: () => apiRequest('/vnc/permitted-nodes', z.object({ nodeIds: z.array(z.uuid()) })),
  });
}
